/*
 * Routes "preview this org template" requests from Apply Brand's own Saved
 * Templates tab into the real Asset Library — the eye icon there doesn't
 * render its own copy of the detail view; it opens the Asset Library (dialog
 * window, or the in-pane overlay where there's no Dialog API) landing
 * directly on that template's detail card.
 *
 * Two halves, because the Asset Library dialog is a genuinely separate
 * browsing context (its own page, `/asset-library-dialog.html`) that shares
 * nothing with the task pane's JS realm except the same origin:
 *   - which template to open: staged in localStorage
 *     so a real Dialog window's own fresh JS
 *     module instance can read it once it mounts.
 *   - "open the library now": an in-memory listener Set — DeckorateModule
 *     (the only place holding the Asset Library launcher) and
 *     SavedTemplatesTab always share the task pane's one JS realm, so no
 *     handoff is needed for this half, only nav-bridge.ts's own pattern.
 */

const STORAGE_KEY = "aiva_deckorate_brand_template_preview_pending";

type Listener = () => void;
const listeners = new Set<Listener>();

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Called by SavedTemplatesTab's eye icon. Stages which template to open the
 *  detail view for, then asks DeckorateModule to open the Asset Library. */
export function requestBrandTemplatePreview(id: string): void {
  getStorage()?.setItem(STORAGE_KEY, id);
  for (const listener of listeners) listener();
}

/** Subscribed by DeckorateModule. Returns an unsubscribe for effect cleanup. */
export function subscribeBrandTemplatePreviewRequest(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether a preview is staged, without consuming it — read by
 *  AssetLibraryContent to decide whether to default to the Brand Templates
 *  tab instead of Slide Templates. */
export function hasPendingBrandTemplatePreview(): boolean {
  return getStorage()?.getItem(STORAGE_KEY) != null;
}

/** Reads and clears the staged id — called once by BrandTemplatesTab as soon
 *  as its list is ready, so a later, unrelated open doesn't replay it. */
export function consumePendingBrandTemplatePreview(): string | null {
  const storage = getStorage();
  const id = storage?.getItem(STORAGE_KEY) ?? null;
  storage?.removeItem(STORAGE_KEY);
  return id;
}

/** Drops the staged id without applying it — called when the Asset Library
 *  closes, so a preview request that never got picked up (e.g. the window
 *  failed to open) can't resurface on some later, unrelated open. */
export function clearPendingBrandTemplatePreview(): void {
  getStorage()?.removeItem(STORAGE_KEY);
}
